/**
 * Chapter Creation Management Component
 * Shows details on every chapter created by each user with analytics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Style as ChapterIcon,
  Assessment as AnalyticsIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { format, formatDistanceToNow } from 'date-fns';
import apiService from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';
import ChapterViewModal from './ChapterViewModal';
import ChapterAnalyticsTab from './ChapterAnalyticsTab';

interface Chapter {
  id: string;
  title: string;
  content: string;
  status: 'draft' | 'published' | 'archived';
  created: string;
  updated: string;
  wordCount: number;
  aiProcessed: boolean;
  processingTime?: number;
  user: {
    id: string;
    email: string;
    name?: string;
  };
  book: {
    id: string;
    title?: string;
  };
  writingStyle?: string | null;
  illustrationStyle?: string | null;
  mood?: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chapter-tabpanel-${index}`}
      aria-labelledby={`chapter-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ChapterCreation: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [tabValue, setTabValue] = useState(0);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalChapters, setTotalChapters] = useState(0);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<any>(null);

  const loadChapters = async () => {
    try {
      setLoading(true);
      const response = await apiService.getChapters({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response && response.chapters) {
        setChapters(response.chapters);
        setTotalChapters(response.totalCount || 0);
      } else {
        setChapters([]);
        setTotalChapters(0);
      }

      dispatch(addNotification({
        type: 'success',
        message: 'Chapter data loaded successfully',
      }));
    } catch (error) {
      console.error('Failed to load chapters:', error);
      setChapters([]);
      setTotalChapters(0);

      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load chapter data - check backend connection',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadChapters();
    dispatch(addNotification({
      type: 'success',
      message: 'Chapter data refreshed',
    }));
  };

  const handleViewChapter = async (chapterId: string) => {
    try {
      const chapterData = await apiService.getChapterById(chapterId);
      setSelectedChapter(chapterData);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('View chapter error:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load chapter details',
      }));
    }
  };

  useEffect(() => {
    loadChapters();
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              mb: spacing[2]
            }}
          >
            <ChapterIcon sx={{ fontSize: 40, color: colors.content.main }} />
            Chapter Creation
          </Typography>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Monitor and manage all chapters created by users across the platform
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: colors.content.main, mb: spacing[6] }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="chapter management tabs"
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.content.main,
              height: 3,
            }
          }}
        >
          <Tab
            label="Chapters Created"
            icon={<ChapterIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
          <Tab
            label="Chapter Analytics"
            icon={<AnalyticsIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
        </Tabs>

        {/* Chapters Created Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Chapter Details</TableCell>
                      <TableCell>Author</TableCell>
                      <TableCell>Book</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Word Count</TableCell>
                      <TableCell>AI Processed</TableCell>
                      <TableCell>Writing Style</TableCell>
                      <TableCell>Illustration Style</TableCell>
                      <TableCell>Mood</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={index}>
                          {Array.from({ length: 11 }).map((_, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      chapters.map((chapter) => (
                        <TableRow key={chapter.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {chapter.title || 'Untitled Chapter'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 300
                              }}>
                                {chapter.content.substring(0, 100)}...
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                <PersonIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {chapter.user?.name || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {chapter.user?.email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {chapter.book?.title || 'No Book'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={
                                chapter.status === 'published' ? 'success' :
                                chapter.status === 'draft' ? 'warning' :
                                'inactive'
                              }
                              label={chapter.status}
                              variant="filled"
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {chapter.wordCount.toLocaleString()} words
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={chapter.aiProcessed ? 'success' : 'inactive'}
                              label={chapter.aiProcessed ? 'Yes' : 'No'}
                              variant="filled"
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {chapter.writingStyle || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {chapter.illustrationStyle || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {chapter.mood || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={format(new Date(chapter.created), 'PPP')}>
                              <Typography variant="body2">
                                {formatDistanceToNow(new Date(chapter.created), { addSuffix: true })}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewChapter(chapter.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalChapters}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Chapter Analytics Tab */}
        <TabPanel value={tabValue} index={1}>
          <ChapterAnalyticsTab />
        </TabPanel>
      </Box>

      {/* View Chapter Modal */}
      <ChapterViewModal
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        chapter={selectedChapter}
      />
    </Box>
  );
};

export default ChapterCreation;
